import logger from '@eventbrite/client-logger';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { Dispatch } from 'redux';
import { ApplePayWithMerchantIdentifier } from '../actions/applePay';
import {
    trackMissingContactInfoInApplePayFixable,
    trackMissingContactInfoInApplePayFixableWithOtherField,
    trackMissingContactInfoInApplePayNonFixable,
} from '../actions/eventAnalytics';
import { APPLE_PAY_FIELD } from '../constants/applePay';
import {
    areAsyncExpressCheckoutEligibleRulesMet,
    areSyncExpressCheckoutEligibleRulesMet,
} from '../selectors/applePay';
import { CheckoutState } from '../state';
import { UserNamespace } from '../state/user';
import { isExpressCheckoutVariant } from './statsig';

/**
 * @WARNING This will throw an error in Safari on organizer sites.
 */
export const isApplePaySupported = () => {
    try {
        return (
            HAS_WINDOW &&
            window.ApplePaySession?.supportsVersion(3) &&
            window.ApplePaySession?.canMakePayments()
        );
    } catch (e) {
        logger.error(e);
        return false;
    }
};

/**
 * @WARNING This will throw an error in Safari on organizer sites.
 */
export const isApplePayCardActive = async (
    applePayInstance: ApplePayWithMerchantIdentifier,
) => {
    try {
        return (
            isApplePaySupported() &&
            (await window.ApplePaySession?.canMakePaymentsWithActiveCard(
                applePayInstance.merchantIdentifier,
            ))
        );
    } catch (e) {
        logger.error(e);
        return false;
    }
};

const getSlicedContactInfo = (fieldValue: string) => {
    return {
        firstName: fieldValue.slice(0, fieldValue.indexOf(' ')),
        lastName: fieldValue.slice(fieldValue.indexOf(' ') + 1),
    };
};

const getPartialValues = (
    dispatch: Dispatch<CheckoutState>,
    definedFieldValue: string,
    missingFieldName: APPLE_PAY_FIELD,
) => {
    const targetFieldValueWords = definedFieldValue.split(' ');

    if (targetFieldValueWords?.length > 1) {
        dispatch(
            trackMissingContactInfoInApplePayFixableWithOtherField(
                missingFieldName,
            ),
        );

        return getSlicedContactInfo(definedFieldValue);
    }

    dispatch(trackMissingContactInfoInApplePayNonFixable(missingFieldName));

    return {
        firstName: definedFieldValue,
        lastName: definedFieldValue,
    };
};

const ERROR_MESSAGE =
    'ApplePay does not provide the contact information required to purchase the ticket.';

const getSanitisedContactInfoField = (
    dispatch: Dispatch<CheckoutState>,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    user?: UserNamespace,
) => {
    const { givenName, familyName } = event.payment?.shippingContact || {};
    const { firstName, lastName } = user || {};

    if (givenName && familyName) {
        return {
            firstName: givenName,
            lastName: familyName,
        };
    }

    if (firstName && lastName) {
        dispatch(
            trackMissingContactInfoInApplePayFixable(
                !givenName
                    ? APPLE_PAY_FIELD.GIVEN_NAME
                    : APPLE_PAY_FIELD.FAMILY_NAME,
            ),
        );

        return {
            firstName,
            lastName,
        };
    }

    if (!familyName && givenName) {
        return getPartialValues(
            dispatch,
            givenName,
            APPLE_PAY_FIELD.FAMILY_NAME,
        );
    }

    if (!givenName && familyName) {
        return getPartialValues(
            dispatch,
            familyName,
            APPLE_PAY_FIELD.GIVEN_NAME,
        );
    }

    throw new Error(ERROR_MESSAGE);
};

export const getApplePayBuyerData = (
    dispatch: Dispatch<CheckoutState>,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    user?: UserNamespace,
) => {
    const emailAddress =
        event.payment?.shippingContact?.emailAddress ?? user?.email;

    if (!emailAddress) {
        throw new Error(ERROR_MESSAGE);
    }

    const { firstName, lastName } = getSanitisedContactInfoField(
        dispatch,
        event,
        user,
    );

    return {
        'N-first_name': firstName,
        'N-last_name': lastName,
        'N-email': emailAddress,
    };
};

export const isExpressCheckoutEnabled = (state: CheckoutState) =>
    areSyncExpressCheckoutEligibleRulesMet(state) &&
    areAsyncExpressCheckoutEligibleRulesMet(state) &&
    isExpressCheckoutVariant();
